<!-- In order to share props from child component to parent component -->
<svelte:options accessors={true} />

<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";
    import { cloneDeep } from "lodash";

    import {
        downloadJson,
        getAmountInCurrency,
        getBillingRuleType,
        getNumberWithComma,
        getOOPType,
        getText,
        isObjectEmpty,
        readJsonFromFileInput,
        showError,
        showInfo,
    } from "../../Utils/utilFunctions.svelte";

    import {
        CardLayout,
        CreateBillingRuleModal,
        LoadingModal,
        NotificationModal,
        PageLoading,
        Pagination,
        SvelteTable,
        TableToggle,
    } from "../../Components/Common/index.svelte";

    import { isMobile, user } from "../../stores";

    import {
        DirectUserRoles,
        pageOptions,
        RuleType,
    } from "../../Utils/utilEnums.svelte";

    import {
        addIcon,
        deleteIcon,
        exportIcon,
        editIcon,
        uploadIcon,
    } from "../../Utils/utilConstants.svelte";
    import { resetAllDefaultBillingRules } from "./helpers/billingRuleHelper.svelte";

    export let params = {};
    export let inCardMode = false;
    export let cardModeConfig = {};
    export let doctorDetails = {};

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    export let returnedBillingRules = [];

    let isLoadingBillingRules = false;

    let isModifyBillingRuleModalOpen = false,
        isDeleteBillingRuleModalOpen = false,
        isModifyingBillingRule = false,
        selectedBillingRule = {};

    let page = 0,
        pageSize = inCardMode ? 999999 : 25,
        fileInput,
        count;

    const columns = [
        {
            key: "name",
            title: "Name",
            headerClass: "text-left pl-4 w-1/4 rounded-tl-xl ",
            class: "text-left pl-4 cursor-default",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "oopType",
            title: "OOP type",
            headerClass: "text-left w-1/8",
            class: "text-left cursor-default",
            renderValue: (v) => getOOPType(v.oopType),
        },
        {
            key: "ruleType",
            title: "Rule type",
            headerClass: "text-left w-1/8",
            class: "text-left cursor-default",
            renderValue: (v) => getBillingRuleType(v.type),
        },
        {
            key: "amount",
            title: "Amount",
            headerClass: "text-left w-1/4",
            class: "text-left cursor-default",
            renderValue: (v) => getAmountInCurrency(v.amount),
        },
        {
            key: "isDefault",
            title: "Default",
            headerClass: "text-left w-1/8",
            class: "text-left cursor-default",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "isDefault",
                    clickFunction: async (rowData) =>
                        handleChangeDefault(rowData),
                },
            },
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left pr-6 pl-3 w-[2%] rounded-tr-xl",
            class: "cursor-default px-3",
            renderValue: (v) => _renderActionIcons(v.isDefault),
        },
    ];

    onMount(async () => {
        // params.id is AccountId if user role is other than doctor
        // params.id is UserId if user role is doctor, need further api call to get its associated accountId

        !isUserDoctor &&
            params &&
            params.id &&
            (await loadBillingRules(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            ));

        isUserDoctor && (await loadDoctorDetails());
    });

    const loadBillingRules = async (start, limit, params) => {
        isLoadingBillingRules = true;

        await apiMethods.getMethod
            .getBillingRules(start, limit, params)
            .then((res) => _handleReturnedBillingRules(res))
            .catch((err) => showError(err));

        isLoadingBillingRules = false;
    };

    // Reload billing rules in modify Presets modal
    export const reloadBillingRules = async () => {
        isLoadingBillingRules = true;

        await apiMethods.getMethod
            .getBillingRules(0, 999999, _getSearchParams())
            .then((res) => _handleReturnedBillingRules(res));

        isLoadingBillingRules = false;
    };

    const loadDoctorDetails = async () => {
        await apiMethods.getMethod
            .getAccountByUserId(Number(params.id))
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    params.id = res.id;
                    doctorDetails = { ...res };
                    await loadBillingRules(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
                }
            });
    };

    const handleChangeDefault = async (data) => {
        if (isModifyingBillingRule) return;

        isModifyingBillingRule = true;

        const { isDefault } = data;

        if (isDefault)
            await apiMethods.putMethod
                .updateBillingRule({ ...data, isDefault: false })
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update billing rule, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else
                        returnedBillingRules = returnedBillingRules.map(
                            (br) => ({
                                ...br,
                                isDefault:
                                    br.id == data.id ? false : br.isDefault,
                            }),
                        );
                })
                .catch((err) => showError(err));
        else {
            await resetAllDefaultBillingRules(returnedBillingRules);

            await apiMethods.putMethod.updateBillingRule({
                ...data,
                isDefault: true,
            });

            returnedBillingRules = returnedBillingRules.map((br) => ({
                ...br,
                isDefault: br.id == data.id,
            }));
        }

        isModifyingBillingRule = false;
    };

    const handleDeleteBillingRule = async () => {
        isModifyingBillingRule = true;

        await apiMethods.deleteMethod
            .deleteBillingRule(selectedBillingRule.id)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete billing rule, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await handleAfterModifyBillingRule();
            })
            .catch((err) => showError(err));

        isModifyingBillingRule = false;

        closeModifyPresetModal();
    };

    const handleAfterModifyBillingRule = async () => {
        await loadBillingRules(page * pageSize, pageSize, _getSearchParams());

        await apiMethods.getMethod
            .getAccountById(Number(params.id))
            .then((res) => (doctorDetails = { ...res }));
    };

    export const openModifyBillingRuleModal = (data = {}) => {
        selectedBillingRule = { ...data };
        isModifyBillingRuleModalOpen = true;
    };

    export const startUploadBillingRule = () => fileInput?.click();

    const openDeleteBillingRuleModal = (data = {}) => {
        if (!data || isObjectEmpty(data)) return;

        selectedBillingRule = { ...data };
        isDeleteBillingRuleModalOpen = true;
    };

    const closeModifyPresetModal = () => {
        isModifyBillingRuleModalOpen = false;
        isDeleteBillingRuleModalOpen = false;
        selectedBillingRule = {};
        isModifyingBillingRule = false;
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("editIcon"))
                openModifyBillingRuleModal(rowData);
            else if (className.includes("deleteIcon"))
                openDeleteBillingRuleModal(rowData);
        }
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        loadBillingRules(0, newPageSize, _getSearchParams());
    };

    let isImporting;

    export const exportBillingRule = () => {
        if (!returnedBillingRules.length)
            return showInfo("No Billing Rules Available");

        const copyData = cloneDeep(returnedBillingRules);
        copyData.forEach((rule) => {
            delete rule["$expanded"];
            delete rule["$selected"];
            delete rule["$sortOn"];
        });

        downloadJson(copyData, `BillingRules`);
    };

    const onFileChange = (e) => {
        const file = e.target.files[0];

        const billingRuleKeys = [
            "name",
            "isDefault",
            "oopType",
            "amount",
            "type",
        ];

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async (e) => {
            const fileData = readJsonFromFileInput(e);
            if (!Array.isArray(fileData)) return;
            const promises = [];
            const hasDefaultRule = returnedBillingRules.some(
                (rule) => rule.isDefault,
            );
            fileData.forEach(async (billingRule) => {
                const importedBillingRule = {};
                billingRuleKeys.forEach((key) => {
                    if (billingRule[key] !== undefined)
                        importedBillingRule[key] = billingRule[key];
                });
                // return if no valid name, ooptype, type, negative amount
                if (
                    !importedBillingRule.name ||
                    (importedBillingRule.oopType !== 0 &&
                        importedBillingRule.oopType !== 1) ||
                    importedBillingRule.type < 2 ||
                    importedBillingRule.amount < 0
                )
                    return showInfo(
                        `Imported billing rule named: ${importedBillingRule.name ?? ""} is invalid, please check data or add manually;`,
                    );
                if (hasDefaultRule) importedBillingRule.isDefault = false;
                importedBillingRule.accountId = doctorDetails.id;
                promises.push(
                    apiMethods.postMethod
                        .addBillingRule(importedBillingRule)
                        .then((res) => {
                            if (!res || res.error)
                                showError(
                                    res.error ??
                                        "Failed to create billing rule, please refresh the page and try again and contact support if the issue persists.",
                                );
                        })
                        .catch((err) => showError(err)),
                );
            });

            fileInput.value = null;

            if (!promises.length) return;

            isImporting = true;
            await Promise.all(promises);
            isImporting = false;
            handleAfterModifyBillingRule();
        };
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        loadBillingRules(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const  sortByDefaultAndName = (items) => {
        return [...items].sort((a, b) => {
            // Default item always on top
            if (a.isDefault && !b.isDefault) return -1;
            if (!a.isDefault && b.isDefault) return 1;

            // Otherwise sort by name
            return a.name.localeCompare(b.name, undefined, {
            sensitivity: 'base'
            });
        });
    }

    // HELPER FUNCTIONS
    const _handleReturnedBillingRules = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load billing rules, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnedBillingRules = [...sortByDefaultAndName(res.results)];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let para = { "filter[doctorId]": Number(params.id) };

        return para;
    };

    const _renderActionIcons = (isDefault = false) => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
            <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Billing Rule">
                <i class="editIcon cursor-pointer ${editIcon}"></i>
            </div>

            <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Billing Rule">
                <i class="deleteIcon cursor-pointer ${deleteIcon}"></i>
            </div>
        </div>`;
    };
</script>

{#if inCardMode}
    <CardLayout
        title={cardModeConfig.title ?? "Billing Rules"}
        btnOptions={cardModeConfig.btnOptions ?? []}
    >
        {#if isLoadingBillingRules}
            <div class="my-6">
                <PageLoading />
            </div>
        {:else}
            <div class="flex flex-1 overflow-auto">
                <SvelteTable
                    {columns}
                    rows={returnedBillingRules}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            </div>
        {/if}
    </CardLayout>
{:else}
    <div class="relative flex-1">
        <div class="flex flex-col gap-y-4 absolute inset-0">
            <div class="flex w-full items-center justify-end gap-x-3">
                {#if returnedBillingRules?.length}
                    <button
                        class="btn btn-sm btn-secondary flex items-center gap-x-2"
                        on:click|preventDefault={exportBillingRule}
                    >
                        <i class={`${exportIcon}`} />
                        <p>Export Billing Rules</p>
                    </button>
                {/if}

                <button
                    class="btn btn-sm btn-secondary flex items-center gap-x-2"
                    on:click|preventDefault={startUploadBillingRule}
                >
                    <i class={`${uploadIcon}`} />
                    <p>Import Billing Rules</p>
                </button>

                <button
                    class="btn btn-sm btn-secondary flex items-center gap-x-2"
                    on:click|preventDefault={() => openModifyBillingRuleModal()}
                >
                    <i class={`${addIcon}`} />
                    <p>Create Billing Rule</p>
                </button>
            </div>

            <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
                {#if isLoadingBillingRules}
                    <PageLoading />
                {:else}
                    <div
                        class={$isMobile
                            ? "overflow-x-scroll overflow-y-visible"
                            : "overflow-y-auto absolute inset-0"}
                    >
                        <SvelteTable
                            {columns}
                            rows={returnedBillingRules}
                            clickCellFunc={({
                                detail: { event, row, key },
                            }) => {
                                handleClickCell(event, row, key);
                            }}
                        />
                    </div>
                {/if}
            </div>

            <div class="flex flex-col">
                <div class="flex flex-1 justify-between items-center">
                    <div class="flex flex-row items-center gap-x-4">
                        <div
                            class="flex items-center {$isMobile
                                ? 'flex-col'
                                : 'flex-row'}"
                        >
                            <strong class="text-gray-600 text-sm"
                                >Table size:</strong
                            >
                            <select
                                class="select select-sm text-gray-600 m-2"
                                bind:value={pageSize}
                                on:change|preventDefault={() =>
                                    onPageSizeChange(pageSize)}
                            >
                                {#each pageOptions as pageOption}
                                    <option
                                        value={pageOption.amount}
                                        selected={pageOption.amount ===
                                            pageSize}
                                    >
                                        {pageOption.amount}
                                    </option>
                                {/each}
                            </select>
                        </div>

                        <strong class="text-gray-600 text-sm">
                            Total records:
                            <span class="text-gray-600"
                                >{getNumberWithComma(count)}</span
                            >
                        </strong>
                    </div>

                    {#if returnedBillingRules.length}
                        <Pagination
                            {page}
                            {pageSize}
                            {count}
                            on:onPageChange={onPageChange}
                        />
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}

<input
    type="file"
    accept=".json"
    style="display: none"
    id="fileInput"
    on:change={onFileChange}
    bind:this={fileInput}
/>

{#if isModifyBillingRuleModalOpen}
    <CreateBillingRuleModal
        closeModal={closeModifyPresetModal}
        onSubmit={handleAfterModifyBillingRule}
        {selectedBillingRule}
        {doctorDetails}
        currentBillingRules={returnedBillingRules}
    />
{/if}

<NotificationModal
    isModalOpen={isDeleteBillingRuleModalOpen}
    title="Delete Selected Billing Rule?"
    isReverseBtns
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    closeModal={handleDeleteBillingRule}
    confirmAction={closeModifyPresetModal}
    isConfirming={isModifyingBillingRule}
    closeModalFunction={closeModifyPresetModal}
>
    <div class="flex flex-col leading-6 gap-y-4">
        <div class="flex flex-1 gap-x-4">
            <p class="title">Billing rule name:</p>
            <p class="flex-1">{getText(selectedBillingRule.name)}</p>
        </div>

        <div class="flex flex-1 gap-x-4">
            <p class="title">OOP type:</p>
            <p class="flex-1">
                {getOOPType(selectedBillingRule.oopType)}
            </p>
        </div>

        <div class="flex flex-1 gap-x-4">
            <p class="title">Billing rule type:</p>
            <p class="flex-1">
                {getBillingRuleType(selectedBillingRule.type)}
            </p>
        </div>

        <div class="flex flex-1 gap-x-4">
            <p class="title">Amount:</p>
            <p class="flex-1">
                {getAmountInCurrency(selectedBillingRule.amount)}
            </p>
        </div>
    </div>
</NotificationModal>

{#if isImporting}
    <LoadingModal titleText="Importing Billing Rules" />
{/if}

<style>
    .title {
        width: 150px;
        text-transform: capitalize;
    }
</style>
